//global imports
import { useState, useEffect, useMemo, useRef } from "react";
import { Button, Switch, Tabs, Timeline } from "antd";
import { get, cloneDeep, groupBy, find, filter, sortBy } from "lodash";

//local imports
import { CanEdit } from "@components/accessLevel";
import { CARD, TIMELINE, cadenceTabs, tabs } from "./constants";
import { NewOutreach } from "./NewOutreach";
import { useLocation } from "react-router";
import { useHelperApiFunctions } from "@utils/customHooks";
import { Spinner } from "@components/healthemSpinner/healthemSpinner";
import DataNotFound from "@components/noDataFound";
import { NO_DATA_AVAILABLE } from "../../constants/constants";
import Icon from "@assets/icon_config";
import Card, { CardBody, CardHeader } from "@components/card";
import "./drawerOutreaches.scss";
import moment from "moment";
import { ALL_OUTREACHES, MY_OUTREACHES, OUTREACH_ADD, dateFormatConfig, negativeOutcomeOfOutreach, positiveOutComeOfOutreach } from "./../../utils/constants";
import { formatDisplayDate, getClientName } from "@utils/helpers";
import InfiniteLoader from "react-window-infinite-loader";
import { VariableSizeList as List } from "react-window";
import useSession from "@utils/useSession";
import { getContentView, getDay } from "./helpers";
import membersService from "@services/members.service";
import { AddCadenceAction, Cadence } from "./Cadence";
import { CadenceContextProvider, useCadenceContext } from "./useCadence";

const { TabPane } = Tabs;

interface header {
  struct_field_id: string;
  label: string;
}

function Outreaches({ members, searchValue, outReachesLoader, outReachesData, fetchOutReaches, setOutReachesData, globalEntryFilterData }: any) {
  const [defaultTab, setDefaultTab] = useState("1");
  const [showAddFrom, setShowAddForm] = useState<boolean>(false);
  const { getStructuredForm, structureLoader, structureData } = useHelperApiFunctions();
  const [onlyMyOutreaches, setOnlyMyOutreaches] = useState<boolean>(false);
  const [page, setPage] = useState(0);
  const session = useSession();

  const formKey = OUTREACH_ADD;

  const location = useLocation();

  const View = useMemo(() => {
      return location.pathname === "/member-detail" ? TIMELINE : CARD;
  },[location.pathname])

  useEffect(() => {
    if (searchValue?.length >= 3 || searchValue?.length === 0) {
      //to fetch the outreaches on change of searchValue
      fetchOutReaches({ searchBy: searchValue, filters: globalEntryFilterData });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchValue]);

  useEffect(() => {
    //to fetch the outreaches when drawer is open
    fetchOutReaches({ searchBy: searchValue, filters: globalEntryFilterData });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    getStructuredForm({ struct_type: formKey });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const tabsChangeHandler = (activeKey: string) => {
    setDefaultTab(activeKey);
  };

  function addNewOutreach() {
    setShowAddForm(true);
  }

  function handlePagination({ page }: { page: number }) {
    fetchOutReaches({ searchBy: searchValue, filters: globalEntryFilterData, page });
  }

  const { groupedData, totalCount , headers , timeLineData} = useMemo(() => {
    const outreaches = cloneDeep(get(outReachesData, "list", []));

    const structureForm = get(structureData, formKey, []);

    const headers : header[] = [];

    const fieldLabelMapping: any = {};

    structureForm.forEach((sf: any) => {
      fieldLabelMapping[sf.struct_field_id] = sf.label;
    });

    structureForm.forEach((d : any) => {
      if(!get(d , 'rule.defaultValue' , '')){
          headers.push({struct_field_id : d.struct_field_id , label : d.label})
      }
    });

    outreaches.forEach((or: any) => {
      or.label = fieldLabelMapping[or.struct_field_id];
    });

    let groupedData: any = groupBy(outreaches, "struct_instance_id");

    groupedData = Object.entries(groupedData);

    groupedData.sort((a: any, b: any) => {
      const timestampA = moment(a[1].find((obj: any) => obj.label === "Outreach Timestamp")?.struct_value);
      const timestampB = moment(b[1].find((obj: any) => obj.label === "Outreach Timestamp")?.struct_value);
      return timestampB.diff(timestampA);
    });

    let timeLineData = cloneDeep(groupedData);
    if (onlyMyOutreaches) {
        timeLineData = filter(groupedData, ([, value]) => {
          return get(value, "[0].caremgr_id", "") === session?.user?.id;
      });
    }

    let totalCount = 0;

    if(View === TIMELINE){
      totalCount = timeLineData.length;
    } else {
      totalCount = get(outReachesData, "count", 0)
    }

    return { groupedData, totalCount , headers , timeLineData};
  }, [View, formKey, onlyMyOutreaches, outReachesData, session?.user?.id, structureData]);

  
  const {
      listCadence,
      cadenceLoader,
      cadencesCount
  } = useCadenceContext()

  useEffect(
      () => {
        listCadence()
      },
      []
  )

  return (
    <Spinner loading={structureLoader || outReachesLoader || cadenceLoader} size={70}>
      <div className="notesWrapper">
        <div className="header">
          <Tabs defaultActiveKey={defaultTab} onChange={tabsChangeHandler} size="large" className="tab-header" destroyInactiveTabPane>
            {tabs?.map((item: any) => {
              return (
                <TabPane tab={`${item.name} (${totalCount})`} key={item.key}>
                    <div className="outreach__wrapper">
                      <OutreachView groupedData={groupedData} totalCount={totalCount} handlePagination={handlePagination} page={page} View={View}
                      setPage={setPage} loading={!(structureLoader || outReachesLoader)} headers={headers} timeLineData={timeLineData} onlyMyOutreaches={onlyMyOutreaches} setOnlyMyOutreaches={setOnlyMyOutreaches}/>
                    </div>
                </TabPane>
              );
            })}
            {
              View === TIMELINE && cadenceTabs?.map((item: any) => {
                return (
                  <TabPane tab={`${item.name} (${cadencesCount})`} key={item.key}>
                    <Cadence />
                  </TabPane>
                );
              })
            }
          </Tabs>
        </div>

        {
          defaultTab === '1' &&
          <>
        <CanEdit>
          <Button type="primary" shape="circle" icon={<span style={{ fontSize: "32px", top: "8%" }} className="icon-plus" />} size="large" className="addNewNote" onClick={addNewOutreach} />
        </CanEdit>

        {showAddFrom && <NewOutreach setShowAddForm={setShowAddForm} members={members} outReachesData={outReachesData} setOutReachesData={setOutReachesData} />}
        </>
        }
        {
          defaultTab === '2' &&
            <AddCadenceAction />
        }
      </div>
    </Spinner>
  );
}

function OutreachView({ groupedData, totalCount, handlePagination, page, setPage , loading , headers , timeLineData , onlyMyOutreaches , setOnlyMyOutreaches , View}: any) {
  if (View === TIMELINE) {
    return <TimeLineView {...{timeLineData, onlyMyOutreaches, setOnlyMyOutreaches }}/>;
  } else {
    return <CardView {...{cardData : groupedData , totalCount , handlePagination , page , setPage , headers}}/>;
  }
}

function CardView({ cardData = [], totalCount = 0, handlePagination, page, setPage , loader , headers}: any) {
  const [height, setheight] = useState<number>(0);

  useEffect(() => {
    let listHeight = document.querySelector(".right-drawer .ant-drawer-body")?.clientHeight || 500;
    let headerHeight = document.querySelector(".right-drawer .ant-drawer-body .header .ant-tabs .ant-tabs-nav")?.clientHeight || 40;

    setheight(listHeight - headerHeight - 60);
  }, []);

  useEffect(() => {
    if (ref) {
      ref.resetAfterIndex(0, true);
    }
  }, [cardData]);

  const [ref, setref] = useState<any>();

  const Row = ({ index, style }: any) => {

    const [uniqueKey, outreach] = cardData[index] || [];
    const ref = useRef(null);

    return uniqueKey ? (
      <div key={uniqueKey} ref={ref} style={style}>
        {<OutreachCard uniqueKey={uniqueKey} outreach={outreach} headers={headers}/>}
      </div>
    ) : null;
  };

  const loadNextFutureReminders = () => {
    if (cardData.length < totalCount) {
      const nextPage: number = page + 1;
      setPage(nextPage);
      handlePagination({ page: nextPage });
    }
  };

  return (
    <Spinner spinning={loader}>
      {totalCount === 0 ? (
        <DataNotFound message={NO_DATA_AVAILABLE} />
      ) : (
        <div>
          {height && (
            <InfiniteLoader
              isItemLoaded={(index) => index < cardData.length}
              itemCount={cardData.length < totalCount ? cardData.length + 1 : cardData.length}
              loadMoreItems={() => loadNextFutureReminders()}
              threshold={1}
            >
              {({ onItemsRendered, ref }: any) => {
                return (
                  <List
                    ref={(ref) => setref(ref)}
                    height={height}
                    width={"100%"}
                    itemCount={cardData.length < totalCount ? cardData.length + 1 : cardData.length}
                    itemSize={(index) => {
                      const [uniqueKey]: any = cardData[index] || [];
                      const card = document.querySelector(`.outreach__wrapper #drawer_outreach_card_${uniqueKey}`);
                      return card?.clientHeight ? card?.clientHeight + 10 : 165; // Added default height of 160 pixels
                    }}
                    onItemsRendered={onItemsRendered}
                  >
                    {Row}
                  </List>
                );
              }}
            </InfiniteLoader>
          )}
        </div>
      )}
    </Spinner>
  );
}

function TimeLineView({ timeLineData , onlyMyOutreaches, setOnlyMyOutreaches}: any) {

  const patientName = useMemo(() => {
    const cMember = membersService?.getCurrentMember;
    return cMember?.patient_name || ''
  }, [])


  function updateOutreaches() {
    setOnlyMyOutreaches((prev: boolean) => {
      return !prev;
    });
  }

  return (
    <div className="outreach__timeline">
      <Card className={`outreach__card`}>
        <CardHeader className={"outreach__timeline__header"}>
          <span className="clr-blueLink w500" style={{ cursor: "auto" }}>
            {patientName}
          </span>
          <span className={"outreach__timeline__header__toggle"}>
            <Switch checked={onlyMyOutreaches} onChange={updateOutreaches} />
            <span className={"outreach__timeline__header__toggle__text"}>{onlyMyOutreaches ? MY_OUTREACHES : ALL_OUTREACHES}</span>
          </span>
        </CardHeader>
        <div className={"outreach__timeline__body custom-scroll-bar"}>
          <Timeline>
            {timeLineData.map((gd: any) => {
              const [key, outreach] = gd;

              const outreachTimeStampData = find(outreach, (o: any) => o.struct_field_id === "2");

              const careManagerName = get(outreachTimeStampData, "caremgr_name", "");

              const outreachTimeStamp = get(outreachTimeStampData, "struct_value", "");

              const programDescription = get(get(outreachTimeStampData, "level", "").split("$|"), "[1]", "");

              const outreachTimeStampDay = getDay(outreachTimeStamp);

              const formattedOutreachTimeStamp = moment(outreachTimeStamp).format(dateFormatConfig.get(getClientName())["dateTimeColumnFilterDisplayFormatWithTime"]);

              const modeOfOutreach = get(
                find(outreach, (o) => o.struct_field_id === "3"),
                "struct_value",
                ""
              );

              const outcomeOfOutreach = get(
                find(outreach, (o) => o.struct_field_id === "4"),
                "struct_value",
                ""
              );

              const timeLineIcon: string = positiveOutComeOfOutreach.includes(outcomeOfOutreach.toLowerCase())
                ? "green-dot"
                : negativeOutcomeOfOutreach.includes(outcomeOfOutreach.toLowerCase())
                ? "red-dot"
                : "grey-dot";

              return (
                <Timeline.Item key={key} dot={<Icon name={timeLineIcon} />}>
                  <div className="outreach__timeline__meta">{`${outreachTimeStampDay}, ${formattedOutreachTimeStamp} | ${careManagerName}`}</div>
                  <div className="outreach__timeline__content">
                    <span>{modeOfOutreach}</span>
                    {outcomeOfOutreach && (
                      <span>
                        <span className="outreach__timeline__content--icon">
                          <Icon name={"small-grey-dot"} />
                        </span>
                        {outcomeOfOutreach}
                      </span>
                    )}
                  </div>
                  {programDescription && <div className="outreach__timeline__content--label">{programDescription}</div>}
                </Timeline.Item>
              );
            })}
          </Timeline>
        </div>
      </Card>
    </div>
  );
}

function OutreachCard({ uniqueKey, outreach , headers}: any) {
  
  const formattedOutreach = useMemo(() => {
    let data = cloneDeep(outreach);
    headers.forEach((header : header) => {
      if(!find(data , (d : any) => {return d.struct_field_id === header.struct_field_id})) {
        data.push({
          ...header,
          'struct_value' : ''
        });
      }
    });
    data = sortBy(data, 'struct_field_id');
    return data;
  },[headers, outreach])

  const location = useLocation();

  const isMemberDetails = location.pathname === "/member-detail";

  const { patientFetchLoader, fetchMemberAndNavigateToProfile } = useHelperApiFunctions();

  const goToMemberDetails = () => {
    if (isMemberDetails) {
      return;
    }
    fetchMemberAndNavigateToProfile({ patientId: get(outreach, "[0].patient_id", "") });
  };

  const getNoteHeaderClassName = () => {
    const baseClass: any = `notesCardHeader`;
    return `${baseClass}`;
  };

  return (
    <div id={`drawer_outreach_card_${uniqueKey}`}>
      <Card className={`outreach__card`}>
        <Spinner loading={patientFetchLoader}>
          <CardHeader className={getNoteHeaderClassName()}>
            <span className="clr-blueLink w500" style={{ cursor: isMemberDetails ? "auto" : "pointer" }} onClick={goToMemberDetails}>
              {get(outreach, "[0].patient_name", "")}
            </span>
          </CardHeader>
        </Spinner>
        <CardBody className="notesCardBody">
          <div style={{ display: "flex", justifyContent: "space-between" }}>
            <span>
              <span className="italic f10 clr-lightGrayColor">{`${formatDisplayDate(
                get(outreach, "[0].created_date", ""),
                "reminderDateActualFormat",
                "defaultDateFormatWithTimeWithPipe",
                true
              )} | ${get(outreach, "[0].caremgr_name", "")}`}</span>
            </span>
          </div>
          {getContentView({ outreach : formattedOutreach })}
        </CardBody>
      </Card>
    </div>
  );
}

export default Outreaches;
